import { useState, useEffect, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { packageService } from "../../services/packageService";
import { debounce } from "../../utils/debounce";

interface Package {
  _id: string;
  title: string;
  courses?: { _id: string; title: string }[];
  price?: number;
  totalCourses?: number;
  validityPeriod?: number | null;
}

interface PackageSelectorProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function PackageSelector({
  selectedIds,
  onChange,
  placeholder = "Search packages...",
  disabled = false,
}: PackageSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPackagesMap, setSelectedPackagesMap] = useState<Record<string, Package>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const fetchPackages = async (term: string) => {
    setIsLoading(true);
    try {
      const response = await packageService.getAdminPackages({
        search: term,
        limit: 50,
      });
      const packagesList = response.data?.packages || response.packages || [];
      setPackages(packagesList);
    } catch (err) {
      console.error("Failed to fetch packages:", err);
      setPackages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetch = useRef(
    debounce((term: string) => {
      fetchPackages(term);
    }, 300)
  );

  useEffect(() => {
    if (isOpen) {
      debouncedFetch.current(searchTerm);
      searchInputRef.current?.focus();
    }
  }, [isOpen, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm("");
    }
  };

  const handleSelectPackage = (packageId: string) => {
    if (!selectedIds.includes(packageId)) {
      onChange([...selectedIds, packageId]);
      const pkg = packages.find((p) => p._id === packageId);
      if (pkg) {
        setSelectedPackagesMap((prev) => ({ ...prev, [packageId]: pkg }));
      }
    }
    setSearchTerm("");
  };

  const handleRemovePackage = (packageId: string) => {
    onChange(selectedIds.filter((id) => id !== packageId));
    setSelectedPackagesMap((prev) => {
      const next = { ...prev };
      delete next[packageId];
      return next;
    });
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      <label className="text-sm font-medium text-gray-700">
        Select Packages
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer flex items-center justify-between text-sm bg-white hover:border-gray-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="text-gray-600">
            {selectedIds.length > 0
              ? `${selectedIds.length} package${selectedIds.length > 1 ? "s" : ""} selected`
              : placeholder}
          </span>
          <div className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
            <Search size={16} className="text-gray-400" />
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
            <div className="p-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50">
              <Search size={14} className="text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                className="w-full bg-transparent border-none outline-none text-sm placeholder:text-gray-400"
                placeholder="Search packages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              {isLoading && <Loader2 size={14} className="animate-spin text-gray-400" />}
            </div>

            <div className="max-h-60 overflow-y-auto">
              {isLoading && packages.length === 0 ? (
                <div className="px-4 py-8 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Loading packages...
                </div>
              ) : packages.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  No packages found
                </div>
              ) : (
                packages.map((pkg) => {
                  const isSelected = selectedIds.includes(pkg._id);
                  return (
                    <div
                      key={pkg._id}
                      className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-indigo-50 text-indigo-700 font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => handleSelectPackage(pkg._id)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="truncate">{pkg.title}</span>
                        {isSelected && (
                          <X size={14} className="text-indigo-500 ml-2" />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {Object.keys(selectedPackagesMap).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {Object.values(selectedPackagesMap).map((pkg) => (
            <div
              key={pkg._id}
              className="flex items-center gap-1 bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-xs"
            >
              <span className="font-medium">{pkg.title}</span>
              {pkg.totalCourses !== undefined && (
                <span className="text-indigo-500">
                  ({pkg.totalCourses} course{pkg.totalCourses !== 1 ? "s" : ""})
                </span>
              )}
              <button
                type="button"
                onClick={() => handleRemovePackage(pkg._id)}
                className="hover:text-indigo-900 ml-1"
                aria-label={`Remove ${pkg.title}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}